import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Avatar, Button, Row, Select, Tooltip } from "@narmi/design_system";
import { Dialog, useNotificationContext } from "cerulean";
import { type Role } from "byzantine/src/Role/Role";
import { useRolesContext } from "byzantine/src/Role/RolesProvider";
import type OrganizationUser from "byzantine/src/OrganizationUser";
import { useNavigate } from "react-router-dom";
import { useOrganizationUserContext } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import { getUsersForRole } from "./OrganizationRoles.helpers";

type DeleteRoleModalProps = {
  role: Role | null;
  isOpen: boolean;
  onClose: () => void;
  organizationUsers?: OrganizationUser[];
  assignableRoles: Role[];
};

type AssignmentMode = "bulk" | "individual";

export default function DeleteRoleModal({
  role,
  isOpen,
  onClose,
  organizationUsers,
  assignableRoles,
}: DeleteRoleModalProps) {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { refresh: refreshRoles, deleteRole } = useRolesContext();
  const { refresh: refreshUsers, updateOrganizationUser } = useOrganizationUserContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReassignRole, setSelectedReassignRole] = useState<string>("");
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>("bulk");
  const [individualAssignments, setIndividualAssignments] = useState<
    Map<string, string>
  >(new Map());

  // Reset form state when modal opens to prevent stale selections
  // from a previous session causing incorrect reassignments
  useEffect(() => {
    if (isOpen) {
      setSelectedReassignRole("");
      setAssignmentMode("bulk");
      setIndividualAssignments(new Map());
    }
  }, [isOpen]);

  // Don't render if no role
  if (!role) {
    return null;
  }

  const usersWithRole = getUsersForRole(role.uuid, organizationUsers);

  const numUsersWithRole = usersWithRole.length;

  const reassignableRoles = assignableRoles.filter((r) => r.uuid !== role.uuid);

  const isDeleteDisabled = (): boolean => {
    if (numUsersWithRole === 0) return false;

    if (assignmentMode === "bulk") {
      return !selectedReassignRole;
    }

    // Individual mode: all users must have a role selected
    if (individualAssignments.size !== numUsersWithRole) return true;
    return Array.from(individualAssignments.values()).some((roleId) => !roleId);
  };

  const onToggle = () => {
    if (assignmentMode === "bulk") {
      setAssignmentMode("individual");
      setSelectedReassignRole("");
      setIndividualAssignments(new Map());
    } else {
      setAssignmentMode("bulk");
      setIndividualAssignments(new Map());
      setSelectedReassignRole("");
    }
  };

  const handleSetIndividualRole = (userUuid: string, roleUuid: string) => {
    const newAssignments = new Map(individualAssignments);
    newAssignments.set(userUuid, roleUuid);
    setIndividualAssignments(newAssignments);
  };

  const reassignUsers = async (
    getRoleUuidForUser: (user: OrganizationUser) => string
  ): Promise<void> => {
    // Sequential reassignment using reduce to chain promises
    // Stops on first error since we don't catch errors in the chain
    await usersWithRole.reduce(async (prevPromise, user) => {
      await prevPromise;
      const targetRoleUuid = getRoleUuidForUser(user);
      if (!targetRoleUuid) {
        throw new Error(
          `No role assigned for user ${user.user.first_name} ${user.user.last_name}`
        );
      }
      const payload: API.UpdateOrganizationUser.Request = {
        first_name: user.user.first_name,
        last_name: user.user.last_name,
        role_uuid: targetRoleUuid,
      };
      await updateOrganizationUser({
        uuid: user.uuid,
        payload,
      });
    }, Promise.resolve());
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      // Step 1: Reassign users if any exist
      if (numUsersWithRole > 0) {
        if (assignmentMode === "bulk") {
          if (selectedReassignRole) {
            await reassignUsers(() => selectedReassignRole);
          }
        } else {
          await reassignUsers(
            (user) => individualAssignments.get(user.uuid) || ""
          );
        }
      }

      // Step 2: Delete the role
      await deleteRole(role.uuid!);

      // Step 3: Success notification
      sendNotification({
        type: "success",
        text: l10n.getString("org-role-delete-success", {
          count: numUsersWithRole,
        }),
      });
      onClose();

      // Step 4: Call callbacks to refresh data
      refreshRoles();
      refreshUsers();
    } catch (error) {
      // Show error notification and stay in modal
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-delete-error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const singleUserName = usersWithRole.length === 1 ? usersWithRole[0].fullName() : "";

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={onClose}
      title={l10n.getString("org-role-delete-title", { name: role.name })}
      width="500px"
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              label={l10n.getString("org-role-delete-close")}
              kind="negative"
              onClick={onClose}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={
                numUsersWithRole > 0
                  ? l10n.getString("org-role-delete-and-reassign")
                  : l10n.getString("org-role-delete-button")
              }
              onClick={handleDelete}
              disabled={isLoading || isDeleteDisabled()}
              isLoading={isLoading}
            />
          </Row.Item>
        </Row>
      }
    >
      {numUsersWithRole > 0 ? (
        <>
          <div>
            {l10n.getString("org-role-delete-confirm", {
              count: numUsersWithRole,
              name: singleUserName,
            })}
          </div>
          <div style={{ marginTop: "16px" }}>
            <span>{l10n.getString("org-role-reassign-users-to")}</span>
            <Select
              value={selectedReassignRole}
              onChange={(value: string) => setSelectedReassignRole(value)}
              disabled={isLoading || assignmentMode === "individual"}
            >
              {reassignableRoles.map((r) => (
                <Select.Item key={r.uuid} value={r.uuid || ""}>
                  {r.name}
                </Select.Item>
              ))}
              <Select.Action
                onSelect={() => {
                  navigate("/roles/new");
                }}
              >
                <Button
                  kind="plain"
                  endIcon="arrow-right"
                  label={l10n.getString("org-role-create-new")}
                />
              </Select.Action>
            </Select>
          </div>
          {numUsersWithRole > 1 && (
            <div style={{ marginTop: "12px" }}>
              <Button
                kind="plain"
                onClick={onToggle}
                endIcon={
                  assignmentMode === "bulk" ? "chevron-down" : "chevron-up"
                }
              >
                {l10n.getString("org-role-assign-individually-button")}
              </Button>
            </div>
          )}
          {assignmentMode === "individual" && numUsersWithRole > 1 && (
            <>
              <div style={{ marginTop: "16px" }}>
                {usersWithRole.map((user) => (
                  <div className="margin--top--m" key={user.uuid}>
                    <Row alignItems="center" gapSize="xs" justifyContent="end">
                      <Row.Item>
                        <Tooltip text={user.user.email}>
                          <Avatar
                            label={user.fullName()}
                            kind="tonal"
                            initials={user.userInitials()}
                          ></Avatar>
                        </Tooltip>
                        {user.fullName()}
                      </Row.Item>
                      <Row.Item shrink>
                        <div style={{ minWidth: "250px" }}>
                          <Select
                            value={individualAssignments.get(user.uuid) || ""}
                            onChange={(value: string) =>
                              handleSetIndividualRole(user.uuid, value)
                            }
                            disabled={isLoading}
                          >
                            <Select.Item value="">
                              {l10n.getString("org-role-assign-select-role")}
                            </Select.Item>
                            {reassignableRoles.map((r) => (
                              <Select.Item key={r.uuid} value={r.uuid || ""}>
                                {r.name}
                              </Select.Item>
                            ))}
                            <Select.Action
                              onSelect={() => {
                                navigate("/roles/new");
                              }}
                            >
                              <Button
                                kind="plain"
                                endIcon="arrow-right"
                                label={l10n.getString("org-role-create-new")}
                              />
                            </Select.Action>
                          </Select>
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div>
          {l10n.getString("org-role-delete-no-users", {
            name: role.name,
          })}
        </div>
      )}
    </Dialog>
  );
}
